// Phase 4a — Audience Personas (display only).
// Phase 4b — adds inline edit: each persona row becomes editable
// (name + description + interests + painPoints + toneHint), with
// add/remove buttons.
// Phase 4 follow-up #6 — avatar generation. Each persona row in edit
// mode gets a "Generate avatar" button that calls the backend
// /api/brand/:id/personas/:index/avatar endpoint to produce a portrait
// illustration via gpt-image-1; the URL is persisted on
// demographics[i].avatarUrl and rendered in place of the generic icon.

import {
  Card, CardBody, VStack, HStack, Box, Text, Heading, Button, Icon, Image,
  Input, Textarea, IconButton, Wrap, WrapItem, Badge, Spinner, useToast,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  NumberInput, NumberInputField, FormControl, FormLabel, FormHelperText, useDisclosure
} from '@chakra-ui/react';
import { useRef, useState } from 'react';
import type { Brand, BrandDemographic } from './types';
import type { BrandEdit } from './useBrandEdit';
import { apiJson } from '../../auth/apiFetch';

// Mirrors server/services/personaGenerationService.js MAX_PERSONAS.
// Keep in sync — the backend also enforces this cap on write.
const MAX_PERSONAS = 20;

const PERSONA_TONES = [
  { bg: 'rgba(122,53,232,0.10)',  fg: '#6B21A8' },
  { bg: 'rgba(245,124,0,0.10)',   fg: '#B45309' },
  { bg: 'rgba(16,184,216,0.10)',  fg: '#0E7490' },
  { bg: 'rgba(16,185,129,0.10)',  fg: '#047857' },
  { bg: 'rgba(217,0,141,0.10)',   fg: '#9D174D' },
  { bg: 'rgba(255,212,0,0.14)',   fg: '#92400E' }
];

export function AudiencePersonasCard({ brand, edit }: { brand: Brand; edit: BrandEdit }) {
  const personas = ((edit.valueOf('demographics') || []) as BrandDemographic[]).slice(0, MAX_PERSONAS);
  const isEditing = edit.isEditing;
  const brandId = brand._id;
  const generateModal = useDisclosure();
  const fileRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  // JSON file upload — reads a .json, expects an array of persona objects,
  // pushes into the edit buffer so the operator can review before Save.
  // Full shape validation happens server-side on PATCH; the client-side
  // check here is just "is this even an array of objects with a name?".
  const onFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // allow re-selecting the same file
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const arr = Array.isArray(parsed) ? parsed : Array.isArray(parsed?.demographics) ? parsed.demographics : null;
      if (!arr) throw new Error('File must be a JSON array of personas, or {"demographics":[...]}');
      const cleaned: BrandDemographic[] = [];
      for (let i = 0; i < arr.length; i++) {
        const d = arr[i];
        if (!d || typeof d !== 'object' || typeof d.name !== 'string' || !d.name.trim()) {
          throw new Error(`Entry ${i} is missing a "name" string`);
        }
        cleaned.push({
          name:        d.name.trim(),
          description: typeof d.description === 'string' ? d.description : '',
          interests:   Array.isArray(d.interests)  ? d.interests.filter((x: unknown) => typeof x === 'string')  : [],
          painPoints:  Array.isArray(d.painPoints) ? d.painPoints.filter((x: unknown) => typeof x === 'string') : [],
          toneHint:    typeof d.toneHint === 'string' ? d.toneHint : ''
        });
      }
      if (cleaned.length > MAX_PERSONAS) {
        toast({ title: `Truncating to ${MAX_PERSONAS} personas (received ${cleaned.length})`, status: 'warning', duration: 4000 });
      }
      edit.setField('demographics', cleaned.slice(0, MAX_PERSONAS));
      toast({
        title: `Loaded ${Math.min(cleaned.length, MAX_PERSONAS)} personas`,
        description: 'Review and click Save Brand to persist. Enrichment will not overwrite them after save.',
        status: 'success',
        duration: 4000
      });
    } catch (err) {
      toast({
        title: 'Upload failed',
        description: err instanceof Error ? err.message : 'Invalid JSON',
        status: 'error',
        duration: 6000
      });
    }
  };

  return (
    <Card variant="outline" h="100%">
      <CardBody>
        <HStack justify="space-between" mb={4} align="flex-start">
          <Heading size="sm" textTransform="uppercase" letterSpacing="0.04em">Audience Personas</Heading>
          <HStack spacing={2}>
            <Button
              size="xs"
              variant="outline"
              onClick={generateModal.onOpen}
              isDisabled={personas.length >= MAX_PERSONAS}
              title={personas.length >= MAX_PERSONAS ? `At the ${MAX_PERSONAS}-persona cap — remove one or replace all via Generate` : 'Generate more personas with an optional brief'}
            >
              ✨ Generate
            </Button>
            {isEditing && (
              <>
                <Button size="xs" variant="outline" onClick={() => fileRef.current?.click()}>
                  Upload JSON
                </Button>
                <Input
                  ref={fileRef}
                  type="file"
                  accept="application/json,.json"
                  display="none"
                  onChange={onFileSelected}
                />
                {personas.length < MAX_PERSONAS && (
                  <Button size="xs" variant="outline" onClick={() => edit.addDemographic()}>
                    + Add persona
                  </Button>
                )}
              </>
            )}
          </HStack>
        </HStack>

        <GeneratePersonasModal
          isOpen={generateModal.isOpen}
          onClose={generateModal.onClose}
          brandId={brandId}
          existingCount={personas.length}
        />

        {personas.length === 0 ? (
          <Text fontSize="xs" color="brand.muted" fontStyle="italic">
            {isEditing
              ? 'Click "Add persona" to start. Personas drive Gemini ad-copy variants.'
              : 'Personas populate during enrichment. Refresh AI to retry, or click Edit Brand to add manually.'}
          </Text>
        ) : (
          <VStack align="stretch" spacing={3}>
            {personas.map((p, i) => (
              <PersonaRow
                key={i}
                index={i}
                persona={p}
                tone={PERSONA_TONES[i % PERSONA_TONES.length]}
                isEditing={isEditing}
                brandId={brandId}
                isDirty={edit.dirty}
                onSet={(next) => edit.setDemographic(i, next)}
                onRemove={() => edit.removeDemographic(i)}
                onAvatarGenerated={(url) => edit.setDemographic(i, { ...p, avatarUrl: url })}
              />
            ))}
          </VStack>
        )}
      </CardBody>
    </Card>
  );
}

function PersonaRow({
  index, persona, tone, isEditing, brandId, isDirty, onSet, onRemove, onAvatarGenerated
}: {
  index: number;
  persona: BrandDemographic;
  tone: { bg: string; fg: string };
  isEditing: boolean;
  brandId: string;
  isDirty: boolean;
  onSet: (persona: BrandDemographic) => void;
  onRemove: () => void;
  onAvatarGenerated: (url: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [generating, setGenerating] = useState(false);
  const toast = useToast();

  // Generation requires the persona to be persisted server-side at the
  // given index — we PATCH avatarUrl onto demographics[i] in place. If
  // the operator is mid-edit with unsaved changes, the index they see
  // may not match what the server has. Block until they save.
  const canGenerate = !!persona.name?.trim() || !!persona.description?.trim();
  const generateAvatar = async () => {
    if (!canGenerate || generating) return;
    if (isDirty) {
      toast({
        title: 'Save changes first',
        description: 'Avatar generation patches the saved persona at this index. Save the brand, then generate.',
        status: 'info',
        duration: 4000
      });
      return;
    }
    setGenerating(true);
    try {
      const res = await apiJson<{ avatarUrl: string }>(`/api/brand/${brandId}/personas/${index}/avatar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      onAvatarGenerated(res.avatarUrl);
      toast({ title: 'Avatar generated', status: 'success', duration: 2500 });
    } catch (err: unknown) {
      toast({
        title: 'Avatar generation failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        status: 'error',
        duration: 5000
      });
    } finally {
      setGenerating(false);
    }
  };

  if (!isEditing) {
    return (
      <HStack align="flex-start" spacing={3}>
        <PersonaTile tone={tone} avatarUrl={persona.avatarUrl} name={persona.name} />
        <Box flex={1} minW={0}>
          <Text fontSize="sm" fontWeight="700" color="brand.ink" noOfLines={1}>
            {persona.name || '(unnamed persona)'}
          </Text>
          {persona.description && (
            <Text fontSize="xs" color="brand.muted" lineHeight="1.5" noOfLines={2}>
              {persona.description}
            </Text>
          )}
        </Box>
      </HStack>
    );
  }

  return (
    <Box borderWidth="1px" borderColor="brand.border" borderRadius="md" p={3}>
      <HStack align="flex-start" spacing={3}>
        <VStack spacing={1.5} align="center">
          <PersonaTile tone={tone} avatarUrl={persona.avatarUrl} name={persona.name} generating={generating} />
          <Button
            size="xs"
            variant="ghost"
            onClick={generateAvatar}
            isDisabled={!canGenerate || generating}
            isLoading={generating}
            loadingText="…"
            fontSize="10px"
            px={1.5}
            h="22px"
          >
            {persona.avatarUrl ? 'Regenerate' : 'Generate'}
          </Button>
        </VStack>
        <VStack flex={1} align="stretch" spacing={2} minW={0}>
          <HStack>
            <Input
              size="sm"
              value={persona.name || ''}
              onChange={e => onSet({ ...persona, name: e.target.value })}
              placeholder={`Persona ${index + 1} name`}
              fontWeight="700"
            />
            <IconButton
              size="sm"
              aria-label="Remove persona"
              variant="ghost"
              colorScheme="red"
              onClick={onRemove}
              icon={<TrashIcon />}
            />
          </HStack>
          <Textarea
            size="sm"
            rows={2}
            value={persona.description || ''}
            onChange={e => onSet({ ...persona, description: e.target.value })}
            placeholder="One-sentence description (≤ 20 words)"
          />
          <Button size="xs" variant="ghost" onClick={() => setExpanded(x => !x)} alignSelf="flex-start">
            {expanded ? '− Hide details' : '+ Edit details (interests, pain points, tone)'}
          </Button>
          {expanded && (
            <VStack align="stretch" spacing={2}>
              <ListEditor
                label="Interests"
                items={persona.interests || []}
                onChange={(next) => onSet({ ...persona, interests: next })}
              />
              <ListEditor
                label="Pain Points"
                items={persona.painPoints || []}
                onChange={(next) => onSet({ ...persona, painPoints: next })}
              />
              <Box>
                <Text fontSize="10px" color="brand.muted" mb={1} textTransform="uppercase" letterSpacing="0.04em">Tone Hint</Text>
                <Input
                  size="sm"
                  value={persona.toneHint || ''}
                  onChange={e => onSet({ ...persona, toneHint: e.target.value })}
                  placeholder="How they speak — e.g. 'direct, results-oriented'"
                />
              </Box>
            </VStack>
          )}
        </VStack>
      </HStack>
    </Box>
  );
}

function PersonaTile({
  tone, avatarUrl, name, generating
}: {
  tone: { bg: string; fg: string };
  avatarUrl?: string;
  name?: string;
  generating?: boolean;
}) {
  if (generating) {
    return (
      <Box w="40px" h="40px" flexShrink={0} borderRadius="md" bg={tone.bg} display="flex" alignItems="center" justifyContent="center">
        <Spinner size="sm" color={tone.fg} />
      </Box>
    );
  }
  if (avatarUrl) {
    return (
      <Image
        src={avatarUrl}
        alt={name ? `${name} avatar` : 'Persona avatar'}
        w="40px" h="40px"
        flexShrink={0}
        borderRadius="md"
        objectFit="cover"
        fallback={
          <Box w="40px" h="40px" flexShrink={0} borderRadius="md" bg={tone.bg} />
        }
      />
    );
  }
  return (
    <Box
      w="40px" h="40px"
      flexShrink={0}
      borderRadius="md"
      bg={tone.bg}
      display="flex"
      alignItems="center"
      justifyContent="center"
      color={tone.fg}
    >
      <Icon viewBox="0 0 24 24" w="20px" h="20px">
        <path fill="currentColor" d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
      </Icon>
    </Box>
  );
}

function ListEditor({
  label, items, onChange
}: {
  label: string;
  items: string[];
  onChange: (next: string[]) => void;
}) {
  const [input, setInput] = useState('');
  const commit = () => {
    const v = input.trim();
    if (!v || items.includes(v)) return;
    onChange([...items, v]);
    setInput('');
  };
  return (
    <Box>
      <Text fontSize="10px" color="brand.muted" mb={1} textTransform="uppercase" letterSpacing="0.04em">{label}</Text>
      <Wrap spacing={1.5}>
        {items.map((item, i) => (
          <WrapItem key={i}>
            <Badge variant="subtle" colorScheme="gray" fontSize="10px" px={1.5} py={1} borderRadius="md" textTransform="none" display="inline-flex" alignItems="center" gap={1}>
              {item}
              <IconButton
                size="xs"
                aria-label={`Remove ${item}`}
                variant="ghost"
                minW="14px"
                h="14px"
                onClick={() => onChange(items.filter(x => x !== item))}
                icon={<Box as="span" fontSize="9px" lineHeight={1}>×</Box>}
              />
            </Badge>
          </WrapItem>
        ))}
      </Wrap>
      <HStack spacing={1.5} mt={1.5}>
        <Input
          size="xs"
          fontSize="11px"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); commit(); }
          }}
          placeholder={`Add ${label.toLowerCase()}…`}
        />
        <Button size="xs" variant="ghost" onClick={commit} isDisabled={!input.trim()}>Add</Button>
      </HStack>
    </Box>
  );
}

function TrashIcon() {
  return <Icon viewBox="0 0 24 24" w="14px" h="14px"><path fill="currentColor" d="M6 19a2 2 0 002 2h8a2 2 0 002-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" /></Icon>;
}

// Prompt-driven persona generation modal. Fires POST /api/brand/:id/personas/generate,
// which calls gpt-4.1 with the operator brief + existing personas as
// context, and returns the updated Brand.demographics array. On success
// we reload the page so BrandContext + useBrandDetail re-read the fresh
// doc — cheaper than plumbing a new mutator through useBrandEdit.
function GeneratePersonasModal({
  isOpen, onClose, brandId, existingCount
}: {
  isOpen: boolean;
  onClose: () => void;
  brandId: string;
  existingCount: number;
}) {
  const [brief, setBrief] = useState('');
  const [count, setCount] = useState(3);
  const [append, setAppend] = useState(true);
  const [busy, setBusy] = useState(false);
  const [orphanRefusal, setOrphanRefusal] = useState<null | {
    totalArtifacts: number;
    totalAds:       number;
    perPersona: Array<{ personaId: string; name?: string | null; artifactCount: number; adCount: number }>;
  }>(null);
  const toast = useToast();

  const roomLeft = Math.max(0, MAX_PERSONAS - existingCount);
  const effectiveMax = append ? Math.min(5, roomLeft) : 5;

  const fire = async (overrideOrphaning: boolean) => {
    setBusy(true);
    try {
      const res = await apiJson<{ appended: number; total: number }>(
        `/api/brand/${brandId}/personas/generate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            brief: brief.trim() || undefined,
            count,
            append,
            overrideOrphaning: overrideOrphaning || undefined
          })
        }
      );
      toast({
        title: `${res.appended} persona(s) generated`,
        description: `Brand now has ${res.total} of ${MAX_PERSONAS}. Reloading…`,
        status: 'success',
        duration: 2500
      });
      // Simplest way to surface the new personas across the whole brand
      // page — no new mutator, no cache invalidation to write. The user
      // sees the toast + reload; net latency is imperceptible.
      setTimeout(() => window.location.reload(), 600);
    } catch (err) {
      // 409 orphan-refuse — server attached orphanCheck to err.data. Show
      // the confirm dialog with counts + names instead of a plain toast.
      const ae = err as { status?: number; data?: { orphanCheck?: unknown } };
      if (ae.status === 409 && ae.data?.orphanCheck) {
        setOrphanRefusal(ae.data.orphanCheck as typeof orphanRefusal extends null ? never : NonNullable<typeof orphanRefusal>);
        setBusy(false);
        return;
      }
      toast({
        title: 'Generation failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        status: 'error',
        duration: 6000
      });
      setBusy(false);
    }
  };

  const submit = () => {
    if (busy) return;
    if (append && roomLeft === 0) {
      toast({ title: `At the ${MAX_PERSONAS}-persona cap`, description: 'Toggle Replace to overwrite, or remove one first.', status: 'warning', duration: 4000 });
      return;
    }
    void fire(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={busy ? () => { /* block close during call */ } : onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Generate audience personas</ModalHeader>
        <ModalCloseButton isDisabled={busy} />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <FormControl>
              <FormLabel fontSize="xs" textTransform="uppercase" letterSpacing="0.04em" color="brand.muted">
                Brief (optional)
              </FormLabel>
              <Textarea
                value={brief}
                onChange={e => setBrief(e.target.value.slice(0, 300))}
                placeholder="e.g. SPF-conscious water sports and outdoor lifestyles"
                rows={3}
                isDisabled={busy}
              />
              <FormHelperText>
                When present, prefers the brief over the brand&apos;s current customer base — useful for testing an adjacent segment.
                Not persisted; one-shot per generation. ({brief.length}/300)
              </FormHelperText>
            </FormControl>

            <HStack spacing={6} align="flex-start">
              <FormControl maxW="120px">
                <FormLabel fontSize="xs" textTransform="uppercase" letterSpacing="0.04em" color="brand.muted">
                  How many
                </FormLabel>
                <NumberInput
                  value={count}
                  min={1}
                  max={effectiveMax || 5}
                  onChange={(_, v) => setCount(Number.isFinite(v) ? v : 1)}
                  isDisabled={busy || (append && roomLeft === 0)}
                >
                  <NumberInputField />
                </NumberInput>
                <FormHelperText>1–5</FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="xs" textTransform="uppercase" letterSpacing="0.04em" color="brand.muted">
                  Mode
                </FormLabel>
                <HStack spacing={2}>
                  <Button
                    size="sm"
                    variant={append ? 'solid' : 'outline'}
                    onClick={() => setAppend(true)}
                    isDisabled={busy}
                  >
                    Append (keep existing)
                  </Button>
                  <Button
                    size="sm"
                    variant={!append ? 'solid' : 'outline'}
                    colorScheme={!append ? 'red' : undefined}
                    onClick={() => setAppend(false)}
                    isDisabled={busy}
                  >
                    Replace all
                  </Button>
                </HStack>
                <FormHelperText>
                  {append
                    ? `Room for ${roomLeft} more (cap ${MAX_PERSONAS}). Existing personas preserved.`
                    : 'Overwrites all existing personas. Cannot be undone from this dialog.'}
                </FormHelperText>
              </FormControl>
            </HStack>

            <Box p={3} bg="brand.canvas" borderRadius="md" borderWidth="1px" borderColor="brand.border">
              <Text fontSize="11px" color="brand.muted">
                Cost: ~$0.02 (single gpt-4.1 call). On success, <code>demographics</code> is auto-pinned so a later brand-enrichment refresh cannot overwrite these.
              </Text>
            </Box>

            {orphanRefusal && (
              <Box p={3} bg="orange.50" borderRadius="md" borderWidth="1px" borderColor="orange.300">
                <Text fontSize="13px" fontWeight="700" color="orange.800" mb={1}>
                  Replace mode would orphan existing data
                </Text>
                <Text fontSize="12px" color="orange.800" mb={2}>
                  {orphanRefusal.totalArtifacts} lifestyle image(s) and {orphanRefusal.totalAds} ad(s) reference personas that would be removed:
                </Text>
                <VStack align="stretch" spacing={1} fontSize="12px" color="orange.900">
                  {orphanRefusal.perPersona.map((p, i) => (
                    <HStack key={i} justify="space-between">
                      <Text fontWeight="600">{p.name || `(persona ${p.personaId.slice(-6)})`}</Text>
                      <Text color="orange.700">
                        {p.artifactCount} image(s){p.adCount > 0 ? ` · ${p.adCount} ad(s)` : ''}
                      </Text>
                    </HStack>
                  ))}
                </VStack>
                <Text fontSize="11px" color="orange.800" mt={2}>
                  Click <b>Proceed anyway</b> below to replace and accept the orphaned references (they&apos;ll stay in Mongo pointing at persona ids that no longer exist). Or <b>Cancel</b> and switch to <b>Append</b> mode.
                </Text>
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={() => { setOrphanRefusal(null); onClose(); }} isDisabled={busy}>Cancel</Button>
          {orphanRefusal ? (
            <Button
              colorScheme="orange"
              onClick={() => { setOrphanRefusal(null); void fire(true); }}
              isLoading={busy}
              loadingText="Generating…"
            >
              Proceed anyway (orphan {orphanRefusal.totalArtifacts + orphanRefusal.totalAds} refs)
            </Button>
          ) : (
            <Button colorScheme="purple" onClick={submit} isLoading={busy} loadingText="Generating…">
              Generate
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
